import {
  CardConfig,
  Placement,
  SectionConfig,
} from "@/shared/domain/dashboards/DashboardDefinition";
import { NumericAggregation } from "@/shared/domain/metrics";

import {
  DashboardLayout,
  DEFAULT_SECTION_ID,
} from "../DashboardHeader/DashboardParams";

/**
 * The "sample dashboard" seed: the demo layout shown by the priority-3
 * landing experience — an org that has metrics but not a single user-visible
 * dashboard yet — and by the picker's "New sample dashboard" action, which
 * lets a user ask for it on purpose at any time. Every other unsaved view
 * ("New empty dashboard", a failed dashboard load) seeds
 * `emptyDashboardLayout` (see `DashboardParams.tsx`) instead.
 */

/**
 * Aggregations the seeded default cards cycle through, per kind, in seed
 * order. The seeded metrics spread across distinct definitions (see
 * `effectiveCardMetric` in `DashboardParams.tsx`)
 * but wrap when the org has fewer definitions than seeded cards; cycling a
 * few aggregations keeps such repeats useful — the same metric shows through
 * a different lens instead of duplicating a card outright.
 */
const SEED_AGGREGATIONS: readonly NumericAggregation[] = ["mean", "max", "min"];

/** Stable ids for the four seed canvases: the first is the shared
 *  {@link DEFAULT_SECTION_ID} (also the single fallback section), the rest
 *  suffix it. */
export function defaultSections(): SectionConfig[] {
  return [
    { id: DEFAULT_SECTION_ID, title: "Section 1" },
    { id: `${DEFAULT_SECTION_ID}-2`, title: "Section 2" },
    { id: `${DEFAULT_SECTION_ID}-3`, title: "Section 3" },
    { id: `${DEFAULT_SECTION_ID}-4`, title: "Section 4" },
  ];
}

/**
 * Default card list for a sample dashboard: a demonstration layout across
 * the four {@link defaultSections} canvases —
 *
 * - Section 1: a 6-tile KPI row over a 4×4 chart beside two stacked 2×2
 *   charts.
 * - Section 2: a row of three 2×2 charts, over a 3×2 cluster of six KPI
 *   tiles beside a 4×3 chart.
 * - Section 3: a 4×4 chart flanked by a column of four KPI tiles on each
 *   side.
 * - Section 4: one full-canvas 6×6 chart over a 6-tile KPI row.
 *
 * Every card carries an explicit placement and section. The ids are literal
 * (`default-s{section}-{kind}-{n}`, extending the original `default-trend-{i}`
 * convention) so they stay stable across renders, which matters for React
 * keys and drag identity. The null metrics resolve to distinct definitions at
 * render time (see `effectiveCardMetric`) and the
 * aggregations cycle through {@link SEED_AGGREGATIONS} per kind. Fields are
 * written in `normalizeCard`'s canonical order so the seed round-trips
 * `buildDashboardDefinition` / `normalizeDefinition` byte-for-byte. Prefer
 * {@link defaultDashboardLayout} when seeding view state.
 */
export function defaultCards(): CardConfig[] {
  const sectionIds = defaultSections().map((s) => s.id);
  const cards: CardConfig[] = [];
  let trendCount = 0;
  let chartCount = 0;
  const trend = (s: number, n: number, placement: Placement) => {
    cards.push({
      id: `default-s${s}-trend-${n}`,
      kind: "trend",
      metric: null,
      aggregation: SEED_AGGREGATIONS[trendCount++ % SEED_AGGREGATIONS.length],
      placement,
      section: sectionIds[s - 1],
    });
  };
  const chart = (s: number, n: number, placement: Placement) => {
    cards.push({
      id: `default-s${s}-chart-${n}`,
      kind: "chart",
      metric: null,
      aggregation: SEED_AGGREGATIONS[chartCount++ % SEED_AGGREGATIONS.length],
      display: "line",
      placement,
      section: sectionIds[s - 1],
    });
  };

  // Section 1: a KPI row over a 4×4 chart beside two stacked 2×2 charts.
  for (let c = 1; c <= 6; c++) {
    trend(1, c, { row: 1, col: c, w: 1, h: 1 });
  }
  chart(1, 1, { row: 2, col: 1, w: 4, h: 4 });
  chart(1, 2, { row: 2, col: 5, w: 2, h: 2 });
  chart(1, 3, { row: 4, col: 5, w: 2, h: 2 });

  // Section 2: a row of three 2×2 charts, over a 3×2 cluster of KPI tiles
  // (rows 3-5 × cols 1-2, row-major) beside a 4×3 chart.
  chart(2, 1, { row: 1, col: 1, w: 2, h: 2 });
  chart(2, 2, { row: 1, col: 3, w: 2, h: 2 });
  chart(2, 3, { row: 1, col: 5, w: 2, h: 2 });
  for (let i = 0; i < 6; i++) {
    trend(2, i + 1, {
      row: Math.floor(i / 2) + 3,
      col: (i % 2) + 1,
      w: 1,
      h: 1,
    });
  }
  chart(2, 4, { row: 3, col: 3, w: 4, h: 3 });

  // Section 3: a 4×4 chart flanked by a column of four KPI tiles on each
  // side (tiles 1-4 down the left edge, 5-8 down the right).
  for (let r = 1; r <= 4; r++) {
    trend(3, r, { row: r, col: 1, w: 1, h: 1 });
  }
  for (let r = 1; r <= 4; r++) {
    trend(3, r + 4, { row: r, col: 6, w: 1, h: 1 });
  }
  chart(3, 1, { row: 1, col: 2, w: 4, h: 4 });

  // Section 4: one full-canvas chart over a KPI row.
  chart(4, 1, { row: 1, col: 1, w: 6, h: 6 });
  for (let c = 1; c <= 6; c++) {
    trend(4, c, { row: 7, col: c, w: 1, h: 1 });
  }

  return cards;
}

/**
 * The full sample seed layout: the four {@link defaultSections} canvases
 * holding {@link defaultCards}. Every seeded card carries its section
 * explicitly, and the sole consumer (`MetricsDashboard`) snapshots the seed
 * through `buildDashboardDefinition`, which re-asserts explicit membership —
 * so no extra assignment pass is needed here.
 */
export function defaultDashboardLayout(): DashboardLayout {
  return { sections: defaultSections(), cards: defaultCards() };
}
